import React from 'react'
import LabeledValue from '../../../../components/common/LabeledValue'
import { TimeOffRequestDetails } from '../../../../store/slices/Requestor/timeOffDetailsSlice'
import { decodeComment } from '../../../../utils/statusFieldsUtils'
import { formatDateToMMDDYY } from "../../../../utils/date"
import { formatDateSeries } from "../../../../utils/dataTransformSeries"
import { useProfileDisplay } from '../../../../hooks/useProfileDisplay'

export type RightPanelVariant = 'full' | 'name-id' | 'name-only'

interface SubmittedActorEntry {
  status?: string | null
  toffReqstStatus?: string | null
  fullName?: string | null
  firstName?: string | null
  middleName?: string | null
  lastName?: string | null
  updatedBy?: string | null
  lastUpdatedBy?: string | null
  createdBy?: string | null
  nuid?: string | null
  empNuid?: string | null
  empNUID?: string | null
}

const normalizeNuid = (value?: string | null): string => (value ?? '').trim().toLowerCase()

const extractNuidFromDisplay = (value?: string | null): string => {
  const trimmedValue = value?.trim() ?? ''
  const openParenIndex = trimmedValue.lastIndexOf('(')
  return openParenIndex >= 0 && trimmedValue.endsWith(')')
    ? trimmedValue.slice(openParenIndex + 1, -1).trim()
    : ''
}

const compactNameParts = (entry?: SubmittedActorEntry): string =>
  [entry?.firstName, entry?.middleName, entry?.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ')

const resolveActorFullName = (entry?: SubmittedActorEntry): string =>
  entry?.middleName?.trim()
    ? compactNameParts(entry) || (entry.fullName ?? '').trim()
    : (entry?.fullName ?? '').trim()

const resolveActorNuid = (...candidates: Array<string | null | undefined>): string =>
  candidates.find((candidate) => candidate?.trim())?.trim() ?? ''

const resolveOwnerNuid = (data: TimeOffRequestDetails): string =>
  resolveActorNuid(data.employee?.empNuid, data.employee?.nuid, data.empNUID, data.nuid)

const normalizeDisplayName = (value?: string | null): string => {
  const withoutProxyPrefix = value?.replace(/^Requestor Proxy\s+/i, '') ?? ''
  const openParenIndex = withoutProxyPrefix.lastIndexOf('(')
  const name = openParenIndex >= 0 ? withoutProxyPrefix.slice(0, openParenIndex) : withoutProxyPrefix
  return name.trim().replaceAll(/\s+/g, ' ').toLowerCase()
}

const resolveOwnerName = (data: TimeOffRequestDetails): string =>
  normalizeDisplayName(
    data.employee?.empFullName ||
      data.employee?.fullName ||
      [data.employee?.firstName, data.employee?.middleName, data.employee?.lastName]
        .filter(Boolean)
        .join(' ') ||
      data.employeeName
  )

const formatSubmittedBy = (fullName: string, nuid: string): string => {
  if (fullName && nuid) return `${fullName} (${nuid})`
  return fullName || nuid
}

const appendMissingNuid = (value: string, nuid: string): string => {
  if (!value || !nuid || extractNuidFromDisplay(value)) return value
  return `${value} (${nuid})`
}

interface RightPanelProps {
  data: TimeOffRequestDetails
  variant?: RightPanelVariant
  showSubmissionDetails?: boolean
  handlePrint?: () => void
}

/**
 * Renders shared request metadata in the status detail right panel.
 * @param data - The request detail payload to display.
 * @param variant - Controls the default field set rendered in the panel.
 * @param showSubmissionDetails - Forces Submitted On and Submitted By into non-full variants when needed.
 * @param handlePrint - Optional print handler for the panel action.
 * @returns The right-side metadata panel for a request status view.
 */
const RightPanel: React.FC<RightPanelProps> = ({
  data,
  variant = 'full',
  showSubmissionDetails = false,
  handlePrint,
}) => {
  // Get logged-in user info as fallback
  const { userInfo: loggedInUserName, userNuid: loggedInUserNuid } = useProfileDisplay()

  // Employee submitted comment - checking in audit history
  const employeeCommentFromAuditHistory = decodeComment(
    data.auditHistory?.find((item: any) => item.toffReqstStatus === 'SUBMITTED')?.comments
  )

  // Employee submitted comment - check in statuses array (if present status is SUBMITTED)
  const employeeCommentFromStatus = decodeComment(
    data.statuses?.find((item: any) => item.status === 'SUBMITTED')?.comments
  )

  // Determine the final employee comment to display, prioritizing audit history over status array.
  const employeeComment = employeeCommentFromAuditHistory || employeeCommentFromStatus

  // Target employee for whom request is raised
  const timeOffRequestForName = data.employee?.empFullName || loggedInUserName
  const timeOffRequestForNuid = data.employee?.empNuid || loggedInUserNuid.replaceAll(/[()]/g, '').trim()
  const timeOffRequestFor = timeOffRequestForName ? `${timeOffRequestForName} (${timeOffRequestForNuid})` : 'N/A'

  // Person who raised the request -> employee / proxy
  // Find SUBMITTED status in statuses wherever it exists
  const submittedStatus = data.statuses?.find((item: SubmittedActorEntry) => item.status === 'SUBMITTED')
  const submittedAudit = data.auditHistory?.find((item: SubmittedActorEntry) => item.toffReqstStatus === 'SUBMITTED')
  let submittedBy = ""
  let submittedByNuid: string | null | undefined

  if (submittedStatus) {
    const submittedByFullName = resolveActorFullName(submittedStatus)
    submittedByNuid = resolveActorNuid(
      submittedStatus.updatedBy,
      submittedStatus.createdBy,
      submittedStatus.nuid,
      submittedStatus.empNuid,
      submittedStatus.empNUID
    )
    submittedBy = formatSubmittedBy(submittedByFullName, submittedByNuid)
  } else {
    const submittedByFullName = resolveActorFullName(submittedAudit)
    submittedByNuid = resolveActorNuid(
      submittedAudit?.lastUpdatedBy,
      submittedAudit?.createdBy,
      submittedAudit?.updatedBy,
      submittedAudit?.nuid,
      submittedAudit?.empNuid,
      submittedAudit?.empNUID
    )
    submittedBy = formatSubmittedBy(submittedByFullName, submittedByNuid)
  }

  // Fall back to the pre-formatted value on the payload (e.g. the post-submit
  // confirmation page, which has no statuses/auditHistory to derive this from).
  if (!submittedBy) {
    submittedBy = data.submittedBy || 'N/A'
  } else if (data.submittedBy && !extractNuidFromDisplay(data.submittedBy) && submittedBy === submittedByNuid) {
    submittedBy = data.submittedBy
  }

  const getSubmittedBy = () => {
    // Only label the submitter a proxy when their nuid actually differs from the
    // request owner's — not merely because manualReqRcvdDate is set.
    const actorNuid = normalizeNuid(submittedByNuid || extractNuidFromDisplay(submittedBy))
    const ownerNuid = normalizeNuid(resolveOwnerNuid(data))
    const actorName = normalizeDisplayName(submittedBy)
    const ownerName = resolveOwnerName(data)
    const isManualProxyActor = Boolean(data.manualReqRcvdDate && actorName && ownerName && actorName !== ownerName)

    if (submittedBy.startsWith('Requestor Proxy ')) {
      return appendMissingNuid(submittedBy, submittedByNuid ?? '')
    }

    if (actorNuid && ((ownerNuid && actorNuid !== ownerNuid) || (!ownerNuid && data.manualReqRcvdDate) || isManualProxyActor)) {
      return `Requestor Proxy ${submittedBy}`
    }
    return submittedBy
  }

  const shouldShowSubmissionDetails = variant === 'full' || showSubmissionDetails

  return (
    <div className="flex-1 flex flex-col">
      <div className="p-4! bg-[#F2F8FB] rounded-3xl">

        {(variant === 'full' || variant === 'name-id') &&
          <LabeledValue
            label="Time Off Request For"
            value={timeOffRequestFor}
          />}

        {/* Display Time Off Request Dates */}
        {(variant === 'full' || variant === 'name-id') &&
          data.requestedDates &&
          data.requestedDates.length > 0 && (
            <div className="mb-6">
              <LabeledValue
                label="Time Off Request Dates"
                value={formatDateSeries(
                  data.requestedDates.map((requestedDate) => requestedDate.date)
                )}
              />
            </div>
          )}

        {(variant === 'full' || variant === 'name-id') && data.requestId && (
          <div className="mb-6">
            <LabeledValue label="Time Off Request ID" value={data.requestId} />
          </div>
        )}
        {shouldShowSubmissionDetails && data.submittedOn && (
          <div className="mb-6">
            <LabeledValue label="Submitted On" value={data.submittedOn} />
          </div>
        )}
        {shouldShowSubmissionDetails && submittedBy && (
          <div className="mb-6">
            <LabeledValue label="Submitted By" value={getSubmittedBy()} />
          </div>
        )}
        {variant === 'full' && data.manualReqRcvdDate && (
          <div className="mb-6">
            <LabeledValue label="Date Manual Request Received" value={formatDateToMMDDYY(data.manualReqRcvdDate)} />
          </div>
        )}
        {variant === 'full' && (
          <div className="mb-6">
            <LabeledValue label="Employee Comments" value={employeeComment ?? ""} />
          </div>
        )}
      </div>
      <div className="flex justify-start mt-4!">
        <button
          type="button"
          className="button -primary flex items-center gap-1"
          onClick={() => handlePrint ? handlePrint() : globalThis.print()}
        >
          <img src="/assets/images/printer-outline.svg" alt="Print" className="w-7 h-7" />
          <span className="align-middle">Print</span>
        </button>
      </div>
    </div>
  )
}

export default RightPanel
